import { Injectable, Inject, InjectionToken } from '@angular/core';
import { Observable } from 'rxjs';
// Models
import { RuleModel } from '../models';
import {
	RuleEngineApiServiceModel,
	AddRulePayload,
	AddRuleCommentPayload,
	AddRuleStipPayload,
	DeleteRuleCommentsPayload,
	DeleteRuleStipsPayload,
	UpdateRuleStatusPayload,
	RulesetForceExecutionPayload
} from '../models/rule-engine-api.model';

/**
 * Constant with the injection token, needed for the dynamic rule-engine.api service injection
 */
export const RULE_ENGINE_API = new InjectionToken<any>('rule-engine.api');

/**
 * RuleEngineApi load factory
 * 
 * @param data 
 * @returns 
 */
export function loadRulesApiConfig(ruleEngineApi: RuleEngineApiServiceModel): RuleEngineApiService {
	const service = new RuleEngineApiService(ruleEngineApi);

	return service;
}

@Injectable()
export class RuleEngineApiService {
	public ruleEngineApiWrapper: RuleEngineApiServiceModel;

	constructor(@Inject(RULE_ENGINE_API) ruleEngineAp: RuleEngineApiServiceModel) {
		this.ruleEngineApiWrapper = ruleEngineAp;
	}

	/**
	 * Returns the set of rules for a particular order
	 * 
	 * @param orderId 
	 * @param ruleSetType 
	 * @param uiSection 
	 * @returns 
	 */
	getOrderRules(orderId: number, ruleSetType: string, uiSection: string = null): Observable<any> {
		return this.ruleEngineApiWrapper.getOrderRules(orderId, ruleSetType, uiSection);
	}

	/**
	 * Update rule status.
	 * 
	 * @param data @see UpdateRuleStatusPayload
	 */
	updateRuleStatus(data: UpdateRuleStatusPayload): Observable<any> {
		return this.ruleEngineApiWrapper.updateRuleStatus(data);
	}

	/**
	 * Forces rule set execution for current order
	 * 
	 * @param data @see RulesetForceExecutionPayload
	 */
	rulesetsForceExecution(data: RulesetForceExecutionPayload): Observable<any> {
		return this.ruleEngineApiWrapper.rulesetsForceExecution(data);
	}

	/**
	 * Creates a new rule.
	 * 
	 * @param data @see AddRuleCommentPayload
	 */
	createRule(data: AddRulePayload): Observable<any> {
		return this.ruleEngineApiWrapper.createRule(data);
	}

	/**
	 * Creates the Rule comment.
	 * 
	 * @param data @see AddRuleCommentPayload
	 */
	createRuleComment(data: AddRuleCommentPayload): Observable<any> {
		return this.ruleEngineApiWrapper.createRuleComment(data);
	}

	/**
	 * Creates a new stip in the RULE
	 * 
	 * @param data @see AddRuleStipPayload
	 */
	createRuleStip(data: AddRuleStipPayload): Observable<any> {
		return this.ruleEngineApiWrapper.createRuleStip(data);
	}

	/**
	 * Bulk delete a set of comments form a rule.
	 * 
	 * @param data @see DeleteRuleCommentsPayload
	 * @returns Observable of forkJoin
	 */
	deleteRuleComments(data: DeleteRuleCommentsPayload): Observable<any> {
		return this.ruleEngineApiWrapper.deleteRuleComments(data);
	}

	/**
	 * Bulk delete a set of stips form a rule.
	 * 
	 * @param data @see DeleteRuleCommentsPayload
	 * @returns Observable of forkJoin
	 */
	deleteRuleStips(data: DeleteRuleStipsPayload): Observable<any> {
		return this.ruleEngineApiWrapper.deleteRuleStips(data);
	}

	/**
	 * Filter the rules and return some of those
	 * 
	 * @param violations @see RuleModel
	 */
	getCustomRulesFilter(violations: RuleModel[]): any {
		return this.ruleEngineApiWrapper.getCustomRulesFilter(violations);
	}
}